import { useCallback, useEffect, useState } from 'react';
import { ApiError } from '@chicare/shared';

import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import { setEnquiryToken } from '@/lib/realtime';
import { Icon } from '@/components/icons/Icon';
import { LiveChat } from './LiveChat';

/**
 * The support widget.
 *
 * ── WHY A WIDGET AND NOT A PAGE OF CHAT ─────────────────────────────────────
 *
 * The first version made the conversation the whole screen. That is a
 * messaging app, and it is the wrong shape for support: it demands the
 * visitor's entire attention for something they want answered while carrying
 * on with what they were doing, and it cannot be dismissed and come back to.
 *
 * A panel opens, is talked into, and is closed. Closing hides it; the
 * conversation is still there on reopening, because the token is kept.
 *
 * ── WHY IT ASKS WHO YOU ARE FIRST ───────────────────────────────────────────
 *
 * One field, before the first message: an email or a phone number. It is not a
 * gate — nobody has to sign in, and that is deliberate, because the people
 * most likely to need support are the ones who cannot get in.
 *
 * It is there so an enquiry can be FOLLOWED UP. A chat that ends with nobody
 * at the desk, or a visitor who closes the tab mid-answer, is a dead end
 * without a way to reach them. With one, it is a call to make tomorrow.
 *
 * ── AND WHY IT ONLY EXISTS ON /support ──────────────────────────────────────
 *
 * Pinned to every screen it would sit on top of whatever somebody is doing —
 * the Add to cart button, on a phone — and hold a socket open on the shop, the
 * checkout and the blog whether or not anybody opened it.
 */

/** Where the enquiry token lives between visits. */
const TOKEN_KEY = 'chicare.support.token';

function readToken(): string | null {
  try {
    return window.localStorage.getItem(TOKEN_KEY);
  } catch {
    // Private window, blocked storage. The widget still works; the visitor
    // just starts a new enquiry next time rather than resuming this one.
    return null;
  }
}

function writeToken(token: string | null): void {
  try {
    if (token === null) window.localStorage.removeItem(TOKEN_KEY);
    else window.localStorage.setItem(TOKEN_KEY, token);
  } catch {
    /* As above. Not worth telling anybody about. */
  }
}

export function SupportWidget({ className }: { className?: string }) {
  const [open, setOpen] = useState(false);
  const [token, setToken] = useState<string | null>(() => readToken());
  const [channel, setChannel] = useState<string | undefined>(undefined);
  const [status, setStatus] = useState<'open' | 'closed'>('open');

  // Whether a stored token has been checked against the server. Only ever
  // relevant WHEN there is one: with no token there is nothing to verify, so
  // readiness is derived rather than set in an effect.
  const [checked, setChecked] = useState(false);
  const ready = !token || checked;

  useEffect(() => {
    if (!token) return;

    // ── BEFORE ANYTHING SUBSCRIBES ──────────────────────────────────────
    // Not in an effect of its own. React runs CHILD effects before parent
    // ones, so a parent effect that rebuilds the socket tears down the
    // subscription LiveChat has just made — the chat subscribes, is silently
    // disconnected, and then receives nothing while looking connected.
    //
    // Set here, where it still runs before `ready` lets the chat mount.
    setEnquiryToken(token);

    let gone = false;

    api
      .enquiry(token)
      .then((data) => {
        if (gone) return;
        setChannel(data.channel);
        setStatus(data.status);
      })
      .catch(() => {
        if (gone) return;
        // 404: the enquiry is not there any more. Forget it and offer a fresh
        // one rather than failing in a way the visitor cannot act on.
        writeToken(null);
        setToken(null);
      })
      .finally(() => {
        if (!gone) setChecked(true);
      });

    return () => {
      gone = true;
    };
  }, [token]);

  const started = useCallback((next: string, nextChannel: string) => {
    // Same reason: the socket has to know which enquiry it is authorising for
    // before the chat mounts and subscribes.
    setEnquiryToken(next);
    writeToken(next);
    setToken(next);
    setChannel(nextChannel);
    setStatus('open');
  }, []);

  return (
    <div className={cn('fixed bottom-20 right-4 z-40 sm:bottom-6', className)}>
      {open ? (
        <Panel
          token={token}
          channel={channel}
          status={status}
          ready={ready}
          onStarted={started}
          onResolved={() => setStatus('closed')}
          onClose={() => setOpen(false)}
        />
      ) : null}

      {/* The launcher. Hidden while the panel is up on a phone, where the two
          would otherwise overlap. */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="support-launcher"
        aria-expanded={open}
        className={cn(
          'ml-auto flex min-h-[52px] items-center gap-2.5 rounded-pill bg-blue px-5 text-white shadow-[0_8px_24px_rgba(15,82,217,0.35)]',
          open ? 'hidden sm:flex' : 'flex',
        )}
      >
        <Icon name="message" size={19} strokeWidth={2} />
        <span className="text-[14px] font-bold">
          {open ? 'Hide chat' : 'Chat with support'}
        </span>
      </button>
    </div>
  );
}

function Panel({
  token,
  channel,
  status,
  ready,
  onStarted,
  onResolved,
  onClose,
}: {
  token: string | null;
  channel?: string;
  status: 'open' | 'closed';
  ready: boolean;
  onStarted: (token: string, channel: string) => void;
  onResolved: () => void;
  onClose: () => void;
}) {
  return (
    <section
      data-testid="support-panel"
      className="mb-3 flex h-[min(78vh,560px)] w-[min(92vw,380px)] flex-col overflow-hidden rounded-[18px] border border-line bg-white shadow-[0_18px_48px_rgba(8,19,43,0.22)]"
    >
      <header className="flex items-center gap-2.5 bg-[linear-gradient(135deg,var(--color-blue-deep),var(--color-blue))] px-4 py-3 text-white">
        <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-white/15">
          <Icon name="message" size={16} strokeWidth={2} />
        </span>

        <span className="min-w-0 flex-1">
          <span className="block text-[13.5px] font-bold">ChiCare support</span>
          <span className="block text-[11px] text-white/70">
            {token ? 'We usually reply in a few minutes' : 'Tell us how to reach you'}
          </span>
        </span>

        <button
          type="button"
          onClick={onClose}
          data-testid="support-close"
          className="grid h-9 w-9 shrink-0 place-items-center rounded-full text-white/80 hover:bg-white/10"
        >
          <span className="sr-only">Close chat</span>
          <Icon name="x" size={17} strokeWidth={2.4} />
        </button>
      </header>

      {!ready ? (
        <p className="m-0 flex-1 px-4 py-8 text-center text-[12.5px] text-faint">Connecting…</p>
      ) : !token ? (
        <StartForm onStarted={onStarted} />
      ) : (
        <Conversation
          token={token}
          channel={channel}
          status={status}
          onResolved={onResolved}
        />
      )}
    </section>
  );
}

/**
 * One contact, before the first message.
 *
 * Name is optional; the contact is not. Validated on the server too, and
 * deliberately strictly — "call me on 0803" is not a number anybody can ring,
 * and finding that out three days later is worse than saying so now, while the
 * person is still here to correct it.
 */
function StartForm({
  onStarted,
}: {
  onStarted: (token: string, channel: string) => void;
}) {
  const [name, setName] = useState('');
  const [contact, setContact] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (contact.trim() === '' || busy) return;

    setBusy(true);
    setError(null);

    try {
      const data = await api.openEnquiry(name.trim(), contact.trim());

      onStarted(data.token, data.channel);
    } catch (e) {
      setError(
        e instanceof ApiError
          ? (e.fieldErrors?.contact?.[0] ?? e.message)
          : 'We could not start the chat. Check your connection and try again.',
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      className="flex flex-1 flex-col gap-3 overflow-y-auto px-4 py-4"
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
    >
      <p className="m-0 text-[12.5px] leading-snug text-muted">
        So we can get back to you if the chat ends before your question does.
      </p>

      <label className="block">
        <span className="mb-1.5 block text-[12.5px] font-medium text-ink">
          Your name <span className="font-normal text-faint">(optional)</span>
        </span>
        <input
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder="Ada Okonkwo"
          data-testid="enquiry-name"
          className="min-h-11 w-full rounded-btn border border-line bg-white px-3 text-[14px] text-ink outline-none focus:border-blue"
        />
      </label>

      <label className="block">
        <span className="mb-1.5 block text-[12.5px] font-medium text-ink">Email or phone</span>
        <input
          value={contact}
          onChange={(event) => setContact(event.target.value)}
          placeholder="you@example.ng or 0803 123 4567"
          data-testid="enquiry-contact"
          autoComplete="email"
          className="min-h-11 w-full rounded-btn border border-line bg-white px-3 text-[14px] text-ink outline-none focus:border-blue"
        />
      </label>

      {error ? (
        <p role="alert" className="m-0 text-[12px] font-medium text-danger">
          {error}
        </p>
      ) : null}

      <button
        type="submit"
        disabled={contact.trim() === '' || busy}
        data-testid="enquiry-start"
        className="mt-auto min-h-11 rounded-btn bg-blue px-4 text-[13.5px] font-bold text-white disabled:opacity-40"
      >
        {busy ? 'Starting…' : 'Start chatting'}
      </button>
    </form>
  );
}

function Conversation({
  token,
  channel,
  status,
  onResolved,
}: {
  token: string;
  channel?: string;
  status: 'open' | 'closed';
  onResolved: () => void;
}) {
  const [resolving, setResolving] = useState(false);

  // Plain dependencies rather than a ref written during render. The token
  // changes at most once per enquiry, so rebuilding these costs one extra
  // load, and reading a ref while rendering is the kind of thing that works
  // until React decides to render twice.
  const load = useCallback(
    async (signal?: AbortSignal) => {
      const data = await api.enquiry(token, signal);

      return {
        messages: data.messages,
        othersTyping: data.staff_typing,
        status: data.status,
      };
    },
    [token],
  );

  const poll = useCallback(
    async (after: number, signal?: AbortSignal) => {
      const data = await api.enquiryPoll(token, after, signal);

      return {
        messages: data.messages,
        othersTyping: data.staff_typing,
        status: data.status,
      };
    },
    [token],
  );

  const send = useCallback((body: string) => api.sendEnquiryMessage(token, body), [token]);
  const typing = useCallback(() => api.enquiryTyping(token), [token]);

  const isMine = useCallback(
    (author: 'buyer' | 'seller' | 'staff' | 'system') =>
      author === 'buyer' || author === 'seller',
    [],
  );

  return (
    <>
      <LiveChat
        className="min-h-0 flex-1 rounded-none border-0"
        load={load}
        poll={poll}
        send={send}
        typing={typing}
        channel={channel}
        isMine={isMine}
        otherParty="ChiCare support"
        placeholder="Type your message"
        empty={
          <p className="m-0 text-[12.5px] leading-snug text-muted">
            Tell us what happened and we will pick it up from here.
          </p>
        }
      />

      {/* "You have answered me", which is not the same as hiding the panel.
          Offered because an enquiry nobody ever closes sits in the desk's Open
          tab for good and makes the queue a lie. */}
      <div className="border-t border-line px-3 py-2">
        {status === 'closed' ? (
          <p className="m-0 text-center text-[11.5px] text-faint">
            Marked as resolved. Write again and it reopens.
          </p>
        ) : (
          <button
            type="button"
            disabled={resolving}
            data-testid="enquiry-resolve"
            onClick={() => {
              setResolving(true);
              void api
                .closeEnquiry(token)
                .then(onResolved)
                .finally(() => setResolving(false));
            }}
            className="min-h-9 w-full text-[11.5px] font-semibold text-muted hover:text-blue"
          >
            {resolving ? 'Closing…' : 'Mark as resolved'}
          </button>
        )}
      </div>
    </>
  );
}
